/**
 * perf-deferred-scripts.js — v2
 * MavenUp Performance Optimization — June 2026
 *
 * GTmetrix confirmed issues this fixes:
 *   - 31.5MB page weight from product hover videos loading on page start
 *   - 1.5s Total Blocking Time from 20+ long JS tasks
 *   - worker.modern.js spawning 7+ incomplete requests
 *
 * Strategy: strip <source src> from all product grid videos on DOM ready,
 * restore via IntersectionObserver when the product card scrolls into view.
 */
(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────
   * 1. PRODUCT GRID VIDEO DEFERRAL
   *    Shopify theme renders hover videos as <video autoplay muted loop>
   *    with <source> tags. Even with preload="none", browsers begin
   *    fetching when src is present. We strip src on load and restore
   *    it lazily via IntersectionObserver.
   * ─────────────────────────────────────────────────────────────*/
  function deferProductGridVideos() {
    if (!('IntersectionObserver' in window)) return;

    // Target video elements inside product hover image containers
    var containers = document.querySelectorAll(
      '.product-item__bg__slide, .product-item__bg__under video, hover-images video'
    );

    if (!containers.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          var el = entry.target;
          var video = el.tagName === 'VIDEO' ? el : el.querySelector('video');
          if (!video) return;

          // Restore stripped sources
          var sources = video.querySelectorAll('source[data-perf-src]');
          sources.forEach(function (s) {
            s.setAttribute('src', s.dataset.perfSrc);
            delete s.dataset.perfSrc;
          });

          if (sources.length) {
            video.load();
            video.play().catch(function () {});
          }

          observer.unobserve(el);
        });
      },
      { rootMargin: '200px 0px', threshold: 0 }
    );

    containers.forEach(function (el) {
      var video = el.tagName === 'VIDEO' ? el : el.querySelector('video');
      if (!video) return;

      // Don't defer hero/slideshow videos — they're above-fold
      if (video.closest('.index-hero, [data-section-type="hero"], [data-section-type="slideshow"]')) {
        return;
      }

      // Strip <source src> to prevent network fetch
      var sources = video.querySelectorAll('source[src]');
      var stripped = 0;
      sources.forEach(function (s) {
        s.dataset.perfSrc = s.getAttribute('src');
        s.removeAttribute('src');
        stripped++;
      });

      if (stripped > 0) {
        video.pause();
        video.load(); // resets internal state, clears buffered data
        observer.observe(el);
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────
   * 2. RUN ON DOM READY
   * ─────────────────────────────────────────────────────────────*/
  function init() {
    deferProductGridVideos();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOMContentLoaded already fired — run on next microtask
    Promise.resolve().then(init);
  }

  /* ─────────────────────────────────────────────────────────────
   * 3. RE-RUN FOR DYNAMICALLY LOADED PRODUCTS (AJAX pagination)
   * ─────────────────────────────────────────────────────────────*/
  if ('MutationObserver' in window) {
    document.addEventListener('DOMContentLoaded', function () {
      var grids = document.querySelectorAll('[data-products-grid], .grid, .collection__products');
      grids.forEach(function (grid) {
        new MutationObserver(function (mutations) {
          var hasNewItems = mutations.some(function (m) {
            return Array.from(m.addedNodes).some(function (n) {
              return n.nodeType === 1 && n.querySelector && n.querySelector('video, .product-item__bg__slide');
            });
          });
          if (hasNewItems) setTimeout(deferProductGridVideos, 50);
        }).observe(grid, { childList: true, subtree: false });
      });
    });
  }

})();
